import { Injectable } from '@angular/core';
import {
  AlignmentType,
  BorderStyle,
  Document,
  Footer,
  Header,
  HorizontalPositionRelativeFrom,
  ImageRun,
  Packer,
  Paragraph,
  Table,
  TableCell,
  TableRow,
  TextRun,
  TextWrappingSide,
  TextWrappingType,
  VerticalAlign,
  VerticalPositionRelativeFrom,
  WidthType,
} from 'docx';
import { saveAs } from 'file-saver';
import { HDate } from '@hebcal/core';
import { Vacation } from '../models/CalendarModels';

export interface VacationFormOptions {
  institutionNumber: string;
  institutionName: string;
  schoolYear: string;
  fromDate: string;
  toDate: string;
  userName: string;
  logo: Blob | null;
  vacations: Vacation[];
}

@Injectable({
  providedIn: 'root',
})
export class VacationWordService {
  private readonly font = 'Arial';
  private readonly headerColor = '1F4E79';
  private readonly tableHeaderFill = '1F6FB2';
  private readonly footerText =
    'לתשומת לב מסמך זה אינו מהווה אישור עבור קיום הסעות בזמן המבוקש. ' +
    'שליחת ההסעות תתבצע בכפוף להיערכות ויכולת מערך ההיסעים';
  private readonly dayNames = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];

  async generateAndDownload(options: VacationFormOptions): Promise<void> {
    const logoImage = options.logo ? await this.loadImage(options.logo) : null;
    const title = `לוח חופשות למוסד ${options.institutionNumber || ''} - ${options.institutionName || ''}`.trim();

    const body: (Paragraph | Table)[] = [];

    if (logoImage) {
      body.push(
        new Paragraph({
          spacing: { after: 0 },
          children: [
            new ImageRun({
              data: logoImage.data,
              transformation: { width: logoImage.width, height: logoImage.height },
              floating: {
                horizontalPosition: {
                  relative: HorizontalPositionRelativeFrom.PAGE,
                  offset: 300000,
                },
                verticalPosition: {
                  relative: VerticalPositionRelativeFrom.PAGE,
                  offset: 220000,
                },
                wrap: { type: TextWrappingType.SQUARE, side: TextWrappingSide.BOTH_SIDES },
                allowOverlap: true,
              },
            }),
          ],
        })
      );
    }

    body.push(this.spacer(160));
    body.push(this.spacer(160));
    body.push(this.spacer(160));

    body.push(
      this.centerLine(`מתאריך ${this.formatDate(options.fromDate)}  (${this.toHebrew(options.fromDate)})`, 24, true, undefined, 120)
    );
    body.push(
      this.centerLine(`עד תאריך ${this.formatDate(options.toDate)}  (${this.toHebrew(options.toDate)})`, 24, true, undefined, 120)
    );
    const reps = this.collectRepresentatives(options.vacations);
    const repText = reps.length ? reps.join(', ') : options.userName || '';
    body.push(this.centerLine(`נציג ממלא: ${repText}`, 22, false, this.headerColor));
    body.push(this.spacer(120));

    body.push(this.buildTable(options.vacations));

    body.push(this.spacer(1500));
    body.push(this.centerLine(`המסמך הופק ביום ${this.nowDate()} בשעה ${this.nowTime()}`, 22, false, this.headerColor));
    body.push(this.centerLine(`על ידי: ${options.userName || ''}`, 22, false, this.headerColor));
    body.push(this.centerLine('נחתם דיגיטלית', 32, true, this.headerColor));

    const doc = new Document({
      styles: {
        default: {
          document: { run: { font: this.font } },
        },
      },
      sections: [
        {
          properties: {},
          headers: {
            default: new Header({
              children: [this.bsdParagraph(), this.titleParagraph(title)],
            }),
          },
          footers: {
            default: new Footer({
              children: [this.footerParagraph(this.footerText)],
            }),
          },
          children: body,
        },
      ],
    });

    const blob = await Packer.toBlob(doc);
    const fileName = `לוח חופשות - ${options.institutionName || options.institutionNumber || 'מוסד'}.docx`;
    saveAs(blob, fileName);
  }

  private collectRepresentatives(vacations: Vacation[]): string[] {
    const candidateKeys = [
      'CreatedOrUpdatedFromPortalBy',
      'new_created_or_updated_from_portal_by',
      'CreatedOrUpdatedByName',
      'LastUpdatedByName',
      'lastUpdatedByName',
    ];
    const reps: string[] = [];
    const seen = new Set<string>();
    (vacations || []).forEach((v) => {
      for (const key of candidateKeys) {
        const val = (v as any)[key];
        if (val && typeof val === 'string' && val.trim()) {
          const name = val.trim();
          if (!seen.has(name)) {
            seen.add(name);
            reps.push(name);
          }
          break;
        }
      }
    });
    return reps;
  }

  private buildTable(vacations: Vacation[]): Table {
    const headers = ['תיאור החופשה', 'ימים', 'שעת סיום', 'החל מתאריך', 'עד תאריך'];

    const headerRow = new TableRow({
      tableHeader: true,
      children: headers.map(
        (h) =>
          new TableCell({
            shading: { fill: this.tableHeaderFill },
            verticalAlign: VerticalAlign.CENTER,
            margins: this.cellMargins(),
            children: [this.cellParagraph(h, true, 'FFFFFF')],
          })
      ),
    });

    const sorted = [...vacations].sort((a, b) => ((a.Date || '') < (b.Date || '') ? -1 : 1));

    const dataRows = sorted.map((v) => {
      const name = v.Period?.viewValue || v.Name || v.Description || '';
      const endTime = v.EndTime?.viewValue || 'חופשה';
      return new TableRow({
        children: [
          this.textCell(name),
          this.textCell(this.dayRange(v.Date, v.DateTo)),
          this.textCell(endTime),
          this.dateCell(v.Date),
          this.dateCell(v.DateTo),
        ],
      });
    });

    return new Table({
      visuallyRightToLeft: true,
      width: { size: 100, type: WidthType.PERCENTAGE },
      borders: {
        top: { style: BorderStyle.SINGLE, size: 4, color: '8EAADB' },
        bottom: { style: BorderStyle.SINGLE, size: 4, color: '8EAADB' },
        left: { style: BorderStyle.SINGLE, size: 4, color: '8EAADB' },
        right: { style: BorderStyle.SINGLE, size: 4, color: '8EAADB' },
        insideHorizontal: { style: BorderStyle.SINGLE, size: 4, color: '8EAADB' },
        insideVertical: { style: BorderStyle.SINGLE, size: 4, color: '8EAADB' },
      },
      rows: [headerRow, ...dataRows],
    });
  }

  private textCell(text: string): TableCell {
    return new TableCell({
      verticalAlign: VerticalAlign.CENTER,
      margins: this.cellMargins(),
      children: [this.cellParagraph(text, false)],
    });
  }

  private dateCell(dateStr?: string): TableCell {
    return new TableCell({
      verticalAlign: VerticalAlign.CENTER,
      margins: this.cellMargins(),
      children: [
        this.cellParagraph(this.formatDate(dateStr), false),
        this.cellParagraph(this.toHebrew(dateStr), false, '666666', 18),
      ],
    });
  }

  private dayRange(from?: string, to?: string): string {
    if (!from) {
      return '';
    }
    const start = this.dayName(from);
    if (!to || to === from) {
      return start;
    }
    const end = this.dayName(to);
    return start === end ? start : `${start}-${end}`;
  }

  private dayName(dateStr: string): string {
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? '' : this.dayNames[d.getDay()];
  }

  private cellMargins() {
    return { top: 120, bottom: 120, left: 120, right: 120 };
  }

  private cellParagraph(text: string, bold: boolean, color?: string, size = 22): Paragraph {
    return new Paragraph({
      bidirectional: true,
      alignment: AlignmentType.CENTER,
      spacing: { before: 40, after: 40 },
      children: [new TextRun({ text: text || '', bold, color, font: this.font, rightToLeft: true, size })],
    });
  }

  private centerLine(text: string, size: number, bold: boolean, color?: string, after?: number): Paragraph {
    const spacing = after != null ? { after } : { before: 60, after: 60 };
    return new Paragraph({
      bidirectional: true,
      alignment: AlignmentType.CENTER,
      spacing,
      children: [new TextRun({ text, bold, color, font: this.font, rightToLeft: true, size })],
    });
  }

  private bsdParagraph(): Paragraph {
    return new Paragraph({
      bidirectional: true,
      alignment: AlignmentType.LEFT,
      spacing: { before: 0, after: 0 },
      children: [new TextRun({ text: 'בס"ד', font: this.font, rightToLeft: true, size: 18 })],
    });
  }

  private titleParagraph(text: string): Paragraph {
    return new Paragraph({
      bidirectional: true,
      alignment: AlignmentType.CENTER,
      spacing: { before: 400, after: 160 },
      children: [
        new TextRun({ text, bold: true, color: this.headerColor, font: this.font, rightToLeft: true, size: 32 }),
      ],
    });
  }

  private footerParagraph(text: string): Paragraph {
    return new Paragraph({
      bidirectional: true,
      alignment: AlignmentType.CENTER,
      children: [new TextRun({ text, color: '808080', font: this.font, rightToLeft: true, size: 24 })],
    });
  }

  private spacer(after = 120): Paragraph {
    return new Paragraph({ text: '', spacing: { after } });
  }

  private toHebrew(dateStr?: string): string {
    if (!dateStr) {
      return '';
    }
    try {
      const d = new Date(dateStr);
      return new HDate(d).renderGematriya().replace(/[֑-ׇ]/g, '');
    } catch {
      return '';
    }
  }

  private formatDate(dateStr?: string): string {
    if (!dateStr) {
      return '';
    }
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) {
      return '';
    }
    const dd = d.getDate().toString().padStart(2, '0');
    const mm = (d.getMonth() + 1).toString().padStart(2, '0');
    return `${dd}/${mm}/${d.getFullYear()}`;
  }

  private nowDate(): string {
    const d = new Date();
    const dd = d.getDate().toString().padStart(2, '0');
    const mm = (d.getMonth() + 1).toString().padStart(2, '0');
    return `${dd}/${mm}/${d.getFullYear()}`;
  }

  private nowTime(): string {
    const d = new Date();
    const hh = d.getHours().toString().padStart(2, '0');
    const min = d.getMinutes().toString().padStart(2, '0');
    return `${hh}:${min}`;
  }

  private loadImage(file: Blob): Promise<{ data: ArrayBuffer; width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const data = reader.result as ArrayBuffer;
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);
        img.onload = () => {
          URL.revokeObjectURL(objectUrl);
          const maxDim = 135;
          const naturalWidth = img.width || maxDim;
          const naturalHeight = img.height || maxDim;
          const scale = Math.min(1, maxDim / Math.max(naturalWidth, naturalHeight));
          const width = Math.round(naturalWidth * scale);
          const height = Math.round(naturalHeight * scale);
          resolve({ data, width, height });
        };
        img.onerror = (e) => {
          URL.revokeObjectURL(objectUrl);
          reject(e);
        };
        img.src = objectUrl;
      };
      reader.onerror = (e) => reject(e);
      reader.readAsArrayBuffer(file);
    });
  }
}
